/*
 * Threshold gifts — spend-based (snippets/cart-gift.liquid) and quantity-based
 * (snippets/quantity-gifts.liquid). Both render the same <cart-gift> element.
 *
 * IMPORTANT — the theme NEVER prices the gift.
 * It only adds a line marked with the `_gift` line item property. What makes the
 * line free is a Shopify AUTOMATIC DISCOUNT configured in the admin on that
 * variant. Without that discount the customer is charged for the gift at
 * checkout. Never fake a zero price on the client.
 */
(() => {
  const GIFT_PROPERTY = '_gift';
  const MAX_PASSES = 4;

  let mutating = false;

  const cartRoute = () => window.routes?.cart_url || '/cart';
  const addRoute = () => window.routes?.cart_add_url || '/cart/add';
  const changeRoute = () => window.routes?.cart_change_url || '/cart/change';

  const postConfig = () =>
    typeof fetchConfig === 'function'
      ? fetchConfig('json')
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  function groupAmount(cents, decimals, thousandsSeparator, decimalSeparator) {
    const fixed = (Number(cents || 0) / 100).toFixed(decimals);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    return parts[1] ? `${whole}${decimalSeparator}${parts[1]}` : whole;
  }

  function formatMoney(cents, format) {
    const template = format || '${{amount}}';
    const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const token = match ? match[1] : 'amount';
    let amount;

    if (token === 'amount_no_decimals') amount = groupAmount(cents, 0, ',', '.');
    else if (token === 'amount_with_comma_separator') amount = groupAmount(cents, 2, '.', ',');
    else if (token === 'amount_no_decimals_with_comma_separator') amount = groupAmount(cents, 0, '.', ',');
    else if (token === 'amount_with_apostrophe_separator') amount = groupAmount(cents, 2, "'", '.');
    else amount = groupAmount(cents, 2, ',', '.');

    return template.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function isGiftLine(item) {
    const properties = item.properties || {};
    return Object.prototype.hasOwnProperty.call(properties, GIFT_PROPERTY) && !!properties[GIFT_PROPERTY];
  }

  /* Gift lines never count toward a threshold: a free line must not unlock itself. */
  function summarize(cart) {
    const state = { lines: [], gifts: new Map() };
    (cart.items || []).forEach((item) => {
      if (isGiftLine(item)) {
        state.gifts.set(String(item.variant_id || item.id), { key: item.key, quantity: item.quantity });
        return;
      }
      state.lines.push({
        productId: String(item.product_id || ''),
        price: item.final_line_price ?? item.line_price ?? 0,
        quantity: item.quantity || 0,
      });
    });
    return state;
  }

  async function fetchCart() {
    try {
      const response = await fetch(`${cartRoute()}.js`, { cache: 'no-store' });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  function sectionsToRender() {
    const targets = [];
    if (document.querySelector('cart-drawer')) {
      targets.push({ id: 'cart-drawer', source: '#CartDrawer', target: '#CartDrawer' });
    }
    const items = document.getElementById('main-cart-items');
    if (items) targets.push({ id: items.dataset.id, source: '.js-contents', target: '#main-cart-items' });
    const footer = document.getElementById('main-cart-footer');
    if (footer) targets.push({ id: footer.dataset.id, source: '.js-contents', target: '#main-cart-footer' });
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', source: '.shopify-section', target: '#cart-icon-bubble' });
    }
    return targets;
  }

  function applySections(state, targets) {
    if (!state || !state.sections) return;
    targets.forEach((target) => {
      const html = state.sections[target.id];
      const root = document.querySelector(target.target);
      if (!html || !root) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(target.source);
      const destination = root.querySelector(target.source) || root;
      if (parsed) destination.innerHTML = parsed.innerHTML;
    });
  }

  async function applyMutation(action) {
    const targets = sectionsToRender();
    const payload = {
      ...action.body,
      sections: targets.map((target) => target.id),
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(action.url, { ...postConfig(), body: JSON.stringify(payload) });
      const state = await response.json();
      // A rejected mutation leaves the cart untouched — degrade quietly.
      if (!response.ok || state.status || state.errors) return false;
      applySections(state, targets);
      return true;
    } catch (error) {
      return false;
    }
  }

  /* A gift line must not be re-priced by the shopper: no quantity bump, no manual
     removal (the threshold owns the line). Dawn's markup is left untouched — the
     row is only flagged here. */
  function lockGiftRow(lineKey) {
    if (!lineKey) return;
    const anchor = document.querySelector(`[data-quantity-line-key="${CSS.escape(lineKey)}"]`);
    const row = anchor?.closest('.cart-item');
    if (!row) return;

    row.classList.add('cart-item--gift');
    row.querySelectorAll('.quantity__input').forEach((input) => {
      input.readOnly = true;
      input.setAttribute('aria-readonly', 'true');
    });
    row.querySelectorAll('.quantity__button').forEach((button) => {
      button.disabled = true;
    });
  }

  function lockGiftRows(state) {
    document.querySelectorAll('.cart-item--gift').forEach((row) => row.classList.remove('cart-item--gift'));
    state.gifts.forEach((line) => lockGiftRow(line.key));
  }

  async function syncAll() {
    if (mutating) return;
    if (!document.querySelector('cart-gift')) return;

    mutating = true;
    let changed = false;

    try {
      for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const cart = await fetchCart();
        if (!cart) return;

        const state = summarize(cart);
        const elements = Array.from(document.querySelectorAll('cart-gift'));
        elements.forEach((element) => element.refresh(state));
        lockGiftRows(state);

        const action = elements.map((element) => element.pendingAction(state)).find(Boolean);
        if (!action) return;

        const applied = await applyMutation(action);
        if (!applied) return;
        changed = true;
      }
    } finally {
      mutating = false;
      if (changed && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift' });
      }
    }
  }

  class CartGift extends HTMLElement {
    connectedCallback() {
      this.messageElement = this.querySelector('[data-gift-message]');
      this.progressElement = this.querySelector('[data-gift-progress]');
      this.fillElement = this.querySelector('[data-gift-progress-fill]');

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'cart-gift') return;
          syncAll();
        });
      }

      lockGiftRow(this.dataset.giftLineKey);

      // Only reach for the network when the server-rendered state already
      // disagrees with what the cart should hold.
      if (this.pendingFromDom()) syncAll();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    get variantId() {
      return this.dataset.variantId || '';
    }

    get threshold() {
      return parseInt(this.dataset.threshold, 10) || 0;
    }

    get autoAdd() {
      return this.dataset.autoAdd !== 'false';
    }

    get available() {
      return this.dataset.available === 'true';
    }

    /* The gift product bought at full price does not qualify either: the Shopify
       discount does not count it, so counting it would add a gift that is charged. */
    progressValue(state) {
      const field = this.dataset.goalType === 'quantity' ? 'quantity' : 'price';
      return state.lines
        .filter((line) => line.productId !== this.dataset.productId)
        .reduce((sum, line) => sum + line[field], 0);
    }

    pendingFromDom() {
      if (!this.autoAdd || !this.available || !this.variantId || this.threshold <= 0) return false;
      const unlocked = this.dataset.unlocked === 'true';
      const inCart = this.dataset.inCart === 'true';
      return unlocked !== inCart;
    }

    pendingAction(state) {
      if (!this.autoAdd || !this.available || !this.variantId || this.threshold <= 0) return null;
      const line = state.gifts.get(this.variantId);
      const unlocked = this.progressValue(state) >= this.threshold;

      if (unlocked && !line) {
        return {
          url: addRoute(),
          body: { items: [{ id: Number(this.variantId), quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }] },
        };
      }
      if (!unlocked && line) {
        return { url: changeRoute(), body: { id: line.key, quantity: 0 } };
      }
      return null;
    }

    refresh(state) {
      const value = this.progressValue(state);
      const unlocked = this.threshold > 0 && value >= this.threshold;
      const remaining = Math.max(this.threshold - value, 0);

      this.dataset.unlocked = String(unlocked);
      this.dataset.inCart = String(state.gifts.has(this.variantId));

      if (this.messageElement) {
        const template = unlocked ? this.dataset.unlockedText : this.dataset.lockedText;
        this.messageElement.textContent = this.buildMessage(template, remaining);
      }

      if (!this.progressElement || !this.fillElement) return;
      const percent = this.threshold > 0 ? Math.min(Math.round((value / this.threshold) * 100), 100) : 0;
      this.fillElement.style.width = `${percent}%`;
      this.progressElement.setAttribute('aria-valuenow', String(percent));
    }

    buildMessage(template, remaining) {
      if (!template) return '';
      const readable =
        this.dataset.goalType === 'quantity'
          ? String(remaining)
          : formatMoney(remaining, this.dataset.moneyFormat);
      return template.replace('[amount]', readable).replace('[remaining]', readable);
    }
  }

  if (!customElements.get('cart-gift')) customElements.define('cart-gift', CartGift);
})();
